import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { deepCamelCaseKeys } from 'src/utils/deepCamelCaseKeys';
import { sessionStore } from 'src/store/sessionStore';
import { auth } from 'src/config/firebase';

export interface ChatMessageClient {
  uid: string;
  fullName: string;
  profilePicture: string | null;
}

export interface ChatLastMessage {
  body: string;
  messageType: string;
  senderUid: string;
  sendTime: string; // ISO string
}

export interface ChatConversationItem {
  chatId: string;
  client: ChatMessageClient;
  lastMessage: ChatLastMessage;
  unreadCount: number;
  isUnread: boolean;
  isActive?: boolean;
  isUnbound?: boolean;
  isSupport?: boolean;
}

export interface FetchTrainerMessagesParams {
  search?: string;
  clientId?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
  paginated?: boolean;
}

export interface TrainerMessagesResponse {
  items: ChatConversationItem[];
  nextCursor: string | null;
  hasMore: boolean;
  truncated?: boolean;
}

export const fetchTrainerMessages = async (
  params: FetchTrainerMessagesParams,
): Promise<TrainerMessagesResponse> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');

  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.clientId && params.clientId !== 'all') queryParams.append('clientId', params.clientId);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.paginated !== undefined) queryParams.append('paginated', params.paginated.toString());

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_MESSAGES}?${queryParams.toString()}`;

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Failed to fetch trainer messages: ${response.status}`);
  }

  const res = await response.json();
  const parsedData = deepCamelCaseKeys(res);

  return parsedData as TrainerMessagesResponse;
};

// We don't use useInfiniteQuery here because we want manual control to merge with Firestore realtime updates
export function useGetTrainerMessagesPage(
  params: FetchTrainerMessagesParams,
  enabled: boolean = true,
) {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid;

  return useQuery({
    queryKey: ['trainer-messages', trainerId, params],
    queryFn: () => fetchTrainerMessages(params),
    enabled: !!trainerId && enabled,
    staleTime: 0, // Always fetch fresh to allow local merge to take over
  });
}
